import React from "react";

import { Progress } from "~/components/ui/progress";
import { formatCurrency } from "~/utils/helpers";

interface CreditUtilizationBarProps {
  totalDebt: number;
  creditLimit: number;
  utilizationPercent: number;
  className?: string;
}

export const CreditUtilizationBar: React.FC<CreditUtilizationBarProps> = ({
  totalDebt,
  creditLimit,
  utilizationPercent,
  className = "",
}) => {
  const available = Math.max(0, creditLimit - totalDebt);

  // If credit limit is 0, show a special case
  if (creditLimit === 0) {
    return (
      <div className={`space-y-2 ${className}`}>
        <div className="flex items-center justify-between text-sm">
          <span className="font-medium text-gray-700">
            Utilización de Crédito
          </span>
          <span className="text-xs text-gray-500">
            (Sin límite establecido)
          </span>
        </div>

        <div className="flex justify-between rounded bg-gray-50 p-2 text-xs text-gray-600">
          <span>
            Disponible:{" "}
            <span className="font-medium text-gray-500">Sin límite</span>
          </span>
          <span>
            Límite: <span className="font-medium">0</span>
          </span>
        </div>
      </div>
    );
  }

  const getUtilizationColor = (percent: number) => {
    if (percent >= 90) return "bg-red-500";
    if (percent >= 75) return "bg-orange-500";
    if (percent >= 50) return "bg-yellow-500";
    return "bg-green-500";
  };

  const getUtilizationLabel = (percent: number) => {
    if (percent >= 100) return "Límite Excedido";
    if (percent >= 90) return "Crítico";
    if (percent >= 75) return "Alto";
    if (percent >= 50) return "Moderado";
    return "Bajo";
  };

  return (
    <div className={`space-y-2 ${className}`}>
      <div className="flex items-center justify-between text-sm">
        <span className="font-medium text-gray-700">
          Utilización de Crédito
        </span>
        <div className="flex items-center gap-2">
          <span
            className={`font-medium ${utilizationPercent >= 90 ? "text-red-600" : "text-gray-900"}`}
          >
            {utilizationPercent.toFixed(1)}%
          </span>
          <span className="text-xs text-gray-500">
            ({getUtilizationLabel(utilizationPercent)})
          </span>
        </div>
      </div>

      <Progress
        value={Math.min(utilizationPercent, 100)}
        className="h-2"
        style={{
          // @ts-ignore - Custom CSS property
          "--progress-background": getUtilizationColor(utilizationPercent),
        }}
      />

      <div className="flex justify-between text-xs text-gray-600">
        <span>
          Disponible:{" "}
          <span className="font-medium text-green-600">
            {formatCurrency(available)}
          </span>
        </span>
        <span>
          Límite:{" "}
          <span className="font-medium">{formatCurrency(creditLimit)}</span>
        </span>
      </div>
    </div>
  );
};
